/**
 * effects/index.js — Effect Registry + Dispatcher
 *
 * Merges all 25 effect executors from 5 category files into a
 * single registry. Provides executeEffect() for the ActionExecutor
 * and helper predicates for deferred/ally-input checks.
 *
 * Part of the Composable Mechanic Engine (Issue #82, Phase A: #175)
 */

import { STATE_EFFECT_EXECUTORS } from './stateEffects';
import { RESOURCE_EFFECT_EXECUTORS } from './resourceEffects';
import { COMBAT_EFFECT_EXECUTORS } from './combatEffects';
import { SOCIAL_EFFECT_EXECUTORS } from './socialEffects';
import { UI_EFFECT_EXECUTORS } from './uiEffects';

// ─── Registry ───────────────────────────────────────────────────────────────

const EFFECT_REGISTRY = {
  ...STATE_EFFECT_EXECUTORS,
  ...RESOURCE_EFFECT_EXECUTORS,
  ...COMBAT_EFFECT_EXECUTORS,
  ...SOCIAL_EFFECT_EXECUTORS,
  ...UI_EFFECT_EXECUTORS,
};

// ─── Default Empty Result ───────────────────────────────────────────────────

const EMPTY_RESULT = {
  characterMutated: false,
  sessionStateUpdates: {},
  deferredAction: null,
  toasts: [],
  allyUpdates: [],
  tabSwitch: null,
  localDiceRoll: null,
};

// ─── Public API ─────────────────────────────────────────────────────────────

/**
 * Execute a single effect.
 *
 * @param {object} effect - { type: string, ...params }
 * @param {object} context - { character, cardKey, sessionState, cooldowns, userInput?, mechanic? }
 * @returns {object} EffectResult with all fields populated (spread from EMPTY_RESULT)
 */
export const executeEffect = (effect, context) => {
  if (!effect || !effect.type) {
    return { ...EMPTY_RESULT, error: 'Invalid effect (missing type)' };
  }

  const executor = EFFECT_REGISTRY[effect.type];
  if (!executor) {
    return { ...EMPTY_RESULT, error: `Unknown effect type: ${effect.type}` };
  }

  const result = executor(effect, context);
  return { ...EMPTY_RESULT, ...result };
};

/**
 * Check if an effect type defers to DiceRoller (stops effect chain).
 * @param {string} effectType
 * @returns {boolean}
 */
export const isDeferredEffect = (effectType) =>
  effectType === 'trigger_spell_attack' ||
  effectType === 'trigger_trait_roll' ||
  effectType === 'trigger_weapon_roll';

/**
 * Check if an effect type requires ally selection input.
 * @param {string} effectType
 * @returns {boolean}
 */
export const requiresAllyInput = (effectType) =>
  effectType === 'heal_ally' ||
  effectType === 'clear_ally_stress' ||
  effectType === 'grant_ally_hope' ||
  effectType === 'grant_ally_armor';

/**
 * Get all registered effect type names.
 * @returns {string[]}
 */
export const getRegisteredEffectTypes = () => Object.keys(EFFECT_REGISTRY);
